import {useTheme} from "@mui/material/styles";
import {useParams} from "react-router-dom";
import React, {useContext, useEffect, useRef, useState} from "react";
import '@livekit/components-styles';
import {FetchContext} from "../../context/FetchContext";
import NewGuestStudio from "./NewGuestStudio";
import NewLobby from "../NewStudio/NewLobby";
import useMediaQuery from "@mui/material/useMediaQuery";
import {doc, onSnapshot} from "firebase/firestore";
import {db} from "../../Firebase";
import {kRoomStateCollectionName} from "../../utils/CollectionConstants";

const NewGuestStudioAndLobby = () => {
    const theme = useTheme();

    const { roomName, userFriendlyRoomName } = useParams();
    const fetchContext = useContext(FetchContext);
    const [preJoinChoices, setPreJoinChoices] = useState(undefined);
    const guestTokenRef = useRef(null);
    const [navigateToStudio, setNavigateToStudio] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [hostIdentity, setHostIdentity] = useState('');
    const [participantIdentifier, setParticipantIdentifier] = useState('');

    // Waiting room state
    const [isWaiting, setIsWaiting] = useState(false);
    const [pendingId, setPendingId] = useState(null);
    const [waitingStatus, setWaitingStatus] = useState('pending'); // 'pending' | 'approved' | 'denied'
    const [guestName, setGuestName] = useState('');

    const readableUserFriendlyRoomName = userFriendlyRoomName ? userFriendlyRoomName.replace(/-/g, ' ') : '';

    const isTooSmall = useMediaQuery(theme.breakpoints.down('smd'));

    // Request to join (creates pending entry, doesn't give token yet)
    const requestToJoin = async (username) => {
        try {
            const {data} = await fetchContext.authAxios.post(`/guest/${roomName}/request`, {
                identity: username,
            }, {
                headers: {
                    Authorization: `Bearer `,
                }
            });
            
            // Store the pending ID and show waiting room
            setPendingId(data.pendingId);
            setGuestName(username);
            setIsWaiting(true);
            setWaitingStatus('pending');
        } catch (err) {
            console.log("Error requesting to join:", err);
            if (err.response?.status === 404) {
                setErrorMessage("The studio you are trying to join does not exist, or the host is not ready yet. Please try again later.");
                return;
            }
            if (err.response?.status === 409) {
                setErrorMessage("There's already a participant in the studio with that name. Use a different name and try again.");
                return;
            }
            if (err.response?.status === 507) {
                setErrorMessage("There's already the maximum number of participants in the studio. Please contact the host.");
                return;
            }
            if (err.response?.status === 423) {
                setErrorMessage("Room is locked. Please try again later.");
                return;
            }
            setErrorMessage("Something went wrong. Please try again later.");
        }
    };

    // Get the actual token after being approved
    const getGuestToken = async () => {
        try {
            const {data} = await fetchContext.authAxios.post(`/guest/${roomName}/token`, {
                pendingId: pendingId,
                identity: guestName,
            }, {
                headers: {
                    Authorization: `Bearer `,
                }
            });
            
            guestTokenRef.current = data.token;
            setHostIdentity(data.hostIdentity);
            setParticipantIdentifier(data.participantIdentifier);
            setNavigateToStudio(true);
        } catch (err) {
            console.log("Error getting guest token:", err);
            // If token fetch fails, show error and go back to lobby
            setIsWaiting(false);
            setPendingId(null);
            setErrorMessage("Failed to join the studio. Please try again.");
        }
    };

    // Listen to Firestore for pending status changes
    useEffect(() => {
        if (!pendingId || !roomName || !isWaiting) {
            return;
        }

        const pendingGuestRef = doc(db, kRoomStateCollectionName, roomName, 'pendingGuests', pendingId);
        
        const unsubscribe = onSnapshot(pendingGuestRef, (docSnapshot) => {
            if (docSnapshot.exists()) {
                const data = docSnapshot.data();
                const newStatus = data.status;
                
                if (newStatus === 'approved' && waitingStatus !== 'approved') {
                    setWaitingStatus('approved');
                    // Automatically fetch token when approved
                    getGuestToken();
                } else if (newStatus === 'denied') {
                    setWaitingStatus('denied');
                }
            } else {
                // Document was deleted - might mean request expired or was cleaned up
                // Go back to lobby
                setIsWaiting(false);
                setPendingId(null);
            }
        }, (error) => {
            console.error('Error listening to pending guest status:', error);
        });

        return () => unsubscribe();
    }, [pendingId, roomName, isWaiting]);

    const handlePreJoinSubmit = async (values) => {
        setPreJoinChoices(values);
        requestToJoin(values.username);
    };

    const handleCancelWaiting = () => {
        // TODO: Optionally call an endpoint to remove the pending entry
        setIsWaiting(false);
        setPendingId(null);
        setWaitingStatus('pending');
        setErrorMessage('');
    };

    // Show studio if approved and token received
    if (navigateToStudio) {
        return (
            <NewGuestStudio 
                userChoices={preJoinChoices} 
                participantIdentifier={participantIdentifier} 
                guestToken={guestTokenRef.current} 
                roomName={roomName} 
                userFriendlyStudioName={readableUserFriendlyRoomName} 
                hostIdentity={hostIdentity}
            />
        );
    }

    // Show lobby - with waiting state if waiting for admission
    return (
        <NewLobby 
            onSubmit={handlePreJoinSubmit} 
            userFriendlyStudioName={readableUserFriendlyRoomName} 
            errorMessage={errorMessage} 
            isHost={false} 
            isSmallScreen={isTooSmall}
            isWaiting={isWaiting}
            waitingStatus={waitingStatus}
            waitingGuestName={guestName}
            onCancelWaiting={handleCancelWaiting}
        />
    );
};

export default NewGuestStudioAndLobby;
